<script lang="ts">
  // BJL-CHECK: Button (used), Spinner (used), Badge (used, via NjBusRouteBadge), Tag, Drawer,
  // List. RailStationSheet's shell, for the same reasons (artifacts/deviations/
  // BusStopSheet.md): the drag-resizable sheet is something Drawer can't express.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors, routeBadgeLabel } from '../../ui/colors'
  import { cardChrome, panelFrame, ROW_RULE, Type } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { formatCountdown, formatShortClock } from '../../ui/transitTime'
  import { useBackHandler } from '../../hooks/useBackHandler.svelte'
  import { useNjBusStore } from '../../store/njbus'
  import { api, type NjBusDeparture } from '../../services/api'
  import { storeSelect } from '../../lib/store.svelte'
  import { useResizableSheet } from '../../lib/resizableSheet.svelte'
  import type { TransferContext } from './timetable'
  import type { NjBusStopInfo } from './stationInfo'
  import TripTimetable from './TripTimetable.svelte'
  import TransferBanner from './TransferBanner.svelte'
  import NjBusRouteBadge from '../NjBusRouteBadge.svelte'
  import SheetBackButton from '../SheetBackButton.svelte'

  interface Props {
    stop: NjBusStopInfo
    onClose: () => void
    /** Reopen the sheet on another stop, chosen from a bus's timetable. */
    onTransfer?: (stop: NjBusStopInfo, ctx: TransferContext | null) => void
    /** Set when reached by transferring: departures before the rider gets here are hidden. */
    transfer?: TransferContext | null
  }

  const { stop, onClose, onTransfer, transfer }: Props = $props()

  const _wh = typeof window !== 'undefined' ? window.innerHeight : 800
  const sheet = useResizableSheet({
    minHeight: 80,
    defaultHeight: Math.round(_wh * 0.5),
    maxHeight: Math.round(_wh * 0.92),
  })

  // The server polls NJ Transit every 15 s; refreshing faster only re-reads the same snapshot.
  const REFRESH_MS = 15_000

  const network = storeSelect(useNjBusStore, (s) => s.network)
  const info = $derived(network.value?.stops.find((s) => s.id === stop.id))

  let departures = $state<NjBusDeparture[] | null>(null)
  let loading = $state(true)
  let failed = $state(false)
  let selectedTrip = $state<NjBusDeparture | null>(null)
  /** One route, or every route — Port Authority's board is 40 buses on 60 routes. */
  let route = $state<string | null>(null)

  useBackHandler(() => selectedTrip !== null, () => { selectedTrip = null })

  const stopId = $derived(stop.id)

  let bodyEl = $state<HTMLDivElement | null>(null)
  const drillKey = $derived(`${stopId}|${selectedTrip ? selectedTrip.tripId : 'board'}`)
  $effect(() => {
    drillKey
    if (bodyEl) bodyEl.scrollTop = 0
  })

  $effect(() => {
    const id = stopId
    let live = true
    const load = () => {
      api
        .getNjBusDepartures(id)
        .then((d) => { if (live) { departures = d.departures; failed = false } })
        .catch(() => { if (live) { departures = departures ?? []; failed = true } })
        .finally(() => { if (live) loading = false })
    }
    loading = true
    departures = null
    selectedTrip = null
    route = null
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  // A transferring rider can see everything with one tap; a fresh transfer re-arms it.
  let showAll = $state(false)
  $effect(() => {
    transfer
    showAll = false
  })
  const cutoff = $derived(transfer && !showAll ? transfer.arriveAt : null)

  // Routes offered as filters: the ones on the board right now, in route order.
  const routes = $derived.by(() => {
    const on = new Set((departures ?? []).map((d) => d.routeId))
    return (info?.routes ?? []).filter((r) => on.has(r))
  })
  const visible = $derived(
    (departures ?? []).filter((d) =>
      (cutoff == null || d.time >= cutoff) && (!route || d.routeId === route)),
  )

  /** "On time", "4 min late", "Cancelled" or "Scheduled". */
  function status(d: NjBusDeparture): { text: string; color: string } {
    if (d.cancelled) return { text: 'Cancelled', color: Colors.danger }
    if (!d.realtime) return { text: 'Scheduled', color: Colors.textMuted }
    const late = Math.round((d.time - d.scheduledTime) / 60)
    return late >= 2
      ? { text: `${late} min late`, color: Colors.danger }
      : { text: 'Live · on time', color: Colors.success }
  }
</script>

{#snippet departureTime(d: NjBusDeparture)}
  {@const label = formatCountdown(d.time, { fallback: '--' })}
  <!-- A countdown under an hour, a clock time beyond it (and always on a transfer). -->
  {#if d.cancelled}
    <span style="color: {Colors.textMuted}; {Type.title}; text-decoration: line-through;">{formatShortClock(d.scheduledTime)}</span>
  {:else if cutoff == null && d.time - Date.now() / 1000 < 3600}
    {@const isNow = label === 'Now'}
    <span style="color: {isNow ? Colors.success : Colors.text}; {Type.title};">
      {isNow ? label : label.replace(' min', '')}
    </span>
    {#if !isNow}<span style="color: {Colors.textSecondary}; {Type.caption};"> min</span>{/if}
  {:else}
    <span style="color: {Colors.text}; {Type.title};">{formatShortClock(d.time)}</span>
  {/if}
{/snippet}

<div
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    height: {sheet.height}px;
    background-color: {Colors.ground};
    {panelFrame('top')}
    z-index: 1000;
    display: flex; flex-direction: column;
    transition: {sheet.dragging ? 'none' : 'height 0.22s cubic-bezier(0.4,0,0.2,1)'};
  "
>
  <!-- Handle row with the chrome on it, laid out exactly as the other stop sheets'. -->
  <div style="position: relative; flex-shrink: 0; display: flex; align-items: center; padding: 4px 18px 2px;">
    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {#if selectedTrip}
        <SheetBackButton onclick={() => { selectedTrip = null }} label="Back to departures" />
      {/if}
    </div>
    <div
      onpointerdown={sheet.onHandleDown}
      role="separator"
      aria-label="Resize stop panel"
      style="flex: 1; align-self: stretch; min-height: 34px; cursor: ns-resize; user-select: none; touch-action: none;"
    ></div>
    <div
      style="
        position: absolute; left: 50%; top: 14px; transform: translateX(-50%);
        width: 36px; height: 4px; border-radius: 2px;
        background-color: rgba(255,255,255,0.25); pointer-events: none;
      "
    ></div>
    <Button variant="text" size="sm" iconOnly onclick={onClose} aria-label="Close stop panel">✕</Button>
  </div>

  <div style="padding: 4px 20px 0; flex-shrink: 0;">
    <div style="color: {Colors.textMuted}; {Type.caption};">NJ Transit bus stop</div>
    <div style="color: {Colors.text}; {Type.heading}; margin-top: 2px; overflow-wrap: anywhere;">
      {stop.name}
    </div>
  </div>

  {#if !selectedTrip && routes.length > 1}
    <!-- Route filter: plain toggle buttons, the one control a busy terminal's board needs. -->
    {#key network.value}
      <div style="display: flex; gap: 8px; padding: 12px 20px 0; flex-shrink: 0; overflow-x: auto;">
        <button
          onclick={() => { route = null }}
          aria-pressed={route === null}
          style="
            flex-shrink: 0; padding: 5px 10px; border-radius: 8px; cursor: pointer;
            border: 1px solid {route === null ? Colors.primary : Colors.borderStrong};
            background: {route === null ? Colors.surfaceElevated : 'none'};
            color: {Colors.text}; {Type.caption}; font-weight: 700;
          "
        >All</button>
        {#each routes as routeId (routeId)}
          <button
            onclick={() => { route = route === routeId ? null : routeId }}
            aria-pressed={route === routeId}
            aria-label="Only route {routeBadgeLabel(routeId)} buses"
            style="
              flex-shrink: 0; display: flex; align-items: center;
              padding: 3px 4px; border-radius: 8px; cursor: pointer;
              border: 1px solid {route === routeId ? Colors.primary : Colors.borderStrong};
              background: {route === routeId ? Colors.surfaceElevated : 'none'};
            "
          >
            <NjBusRouteBadge {routeId} size="xs" />
          </button>
        {/each}
      </div>
    {/key}
  {/if}

  <div bind:this={bodyEl} style="overflow-y: auto; flex: 1; min-height: 0; padding: 0 20px {safeBottom(28)};">
    {#if selectedTrip}
      <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 10px;">
        {#key network.value}<NjBusRouteBadge routeId={selectedTrip.routeId} size="md" />{/key}
        <div style="flex: 1; min-width: 0;">
          <div style="color: {Colors.text}; {Type.title}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
            To {selectedTrip.headsign}
          </div>
          <div style="color: {Colors.textMuted}; {Type.caption};">
            NJ Transit · leaves {formatShortClock(selectedTrip.scheduledTime)}
          </div>
        </div>
      </div>
      <TripTimetable
        source={{ kind: 'njbus', tripId: selectedTrip.tripId }}
        routeId={selectedTrip.routeId}
        currentStopId={stop.id}
        onTransfer={(stopId, stopName, ctx) => onTransfer?.({ id: stopId, name: stopName }, ctx)}
      />
    {:else if loading && !departures}
      <div style="display: flex; align-items: center; gap: 8px; padding: 10px 0; color: {Colors.textMuted}; {Type.body};">
        <Spinner size="sm" label="Loading buses" /> Loading buses…
      </div>
    {:else}
      {#if transfer}
        <TransferBanner ctx={transfer} active={!showAll} onToggle={() => { showAll = !showAll }} />
      {/if}
      {#if visible.length > 0}
        <!-- Every bus in time order across routes: where it is going on the leading edge and
             when it leaves on the trailing one. -->
        <div style="{cardChrome()} padding: 0 14px; margin-top: 12px;">
          {#key network.value}
            {#each visible as d, i (d.tripId + d.scheduledTime)}
              {@const st = status(d)}
              <button
                onclick={() => { selectedTrip = d }}
                aria-label="See the stops of the {formatShortClock(d.scheduledTime)} {routeBadgeLabel(d.routeId)} bus to {d.headsign}"
                style="
                  display: flex; align-items: center; gap: 12px; padding: 11px 0;
                  background: none; border: none;
                  {i === visible.length - 1 ? '' : `border-bottom: ${ROW_RULE};`}
                  cursor: pointer; text-align: left; width: 100%;
                  opacity: {d.cancelled ? 0.65 : 1};
                "
              >
                <NjBusRouteBadge routeId={d.routeId} />
                <div style="flex: 1; min-width: 0;">
                  <div style="color: {Colors.text}; {Type.bodyLg}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    To {d.headsign}
                  </div>
                  <div style="color: {Colors.textMuted}; {Type.caption}; margin-top: 2px;">
                    <span style="color: {st.color};">{st.text}</span>
                  </div>
                </div>
                <div style="text-align: right; flex-shrink: 0;">{@render departureTime(d)}</div>
                <span aria-hidden="true" style="flex-shrink: 0; color: rgba(255,255,255,0.35); font-size: 15px;">›</span>
              </button>
            {/each}
          {/key}
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          {failed
            ? 'Couldn’t reach the NJ Transit timetable. Retrying…'
            : cutoff != null
              ? `No buses leave here after ${formatShortClock(cutoff)} in the next two hours.`
              : 'No buses leave here in the next two hours.'}
        </div>
      {/if}
    {/if}
  </div>
</div>
